/**
 * ProviderChangeSection - Section wrapper for provider change cards
 *
 * Matches the EntityChangeSection header pattern (title + count badge)
 * but renders rich ProviderChangeCard components instead of generic rows.
 * Sorts changed providers (CREATED, UPDATED, DELETED) above SAME ones.
 */

import { useMemo } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type {
  ChangedProviderEntity,
  ChangedTreatmentRequestEntity,
} from '@/services/api/generated/crossMatterChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { ProviderChangeCard } from './ProviderChangeCard';

interface ProviderChangeSectionProps {
  providers: ChangedProviderEntity[];
  treatmentRequests?: ChangedTreatmentRequestEntity[];
}

export const ProviderChangeSection = ({
  providers,
  treatmentRequests = [],
}: ProviderChangeSectionProps) => {
  const sorted = useMemo(
    () =>
      [...providers].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [providers],
  );

  // Group treatment requests by provider_id
  const trByProvider = useMemo(
    () =>
      treatmentRequests.reduce<Record<string, ChangedTreatmentRequestEntity[]>>(
        (map, tr) => {
          const providerId = (tr.current ?? tr.old)?.provider_id;
          if (providerId) {
            (map[providerId] ??= []).push(tr);
          }
          return map;
        },
        {},
      ),
    [treatmentRequests],
  );

  if (providers.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Providers</h3>
        <Badge color="zinc" className="text-xs">
          {providers.length}
        </Badge>
      </div>
      <div className="space-y-2">
        {sorted.map((provider) => (
          <ProviderChangeCard
            key={provider.entity_id}
            entity={provider}
            treatmentRequests={trByProvider[provider.entity_id]}
          />
        ))}
      </div>
    </div>
  );
};
